/*
 * ContactDetails
 *
 * Features:
 * - Renders schema-driven contact detail sections with permission-aware editing
 * - Persists contact changes through the contact data route
 * - Supports delete and cancel flows for contact management
 *
 * Implementation:
 * - Only editable schemas can submit updates
 * - Save requests preserve the current contact display name when the active
 *   schema section omits name/displayName fields
 * - Notifications communicate update and delete outcomes to the user
 *
 * Changes:
 * - 2026-08-18: Hides unauthorized Franchise Contact sections and relies on page scrolling
 * - 2026-09-04: Shows permission-gated Franchise fields from every RLP Contact Overview path
 * - 2026-08-18: Presents Franchise Contact Role as a frontend-owned dropdown
 * - 2026-08-18: Enables Franchise Contact edits for users with its configured Admin team access
 * - 2026-08-06: Added cache-coherent update/delete mutations and router navigation
 * - 2026-07-22: Increased field value text to text-sm and tightened vertical
 *   row spacing for the Contact Overview form
 * - 2026-04-28: Prevented schema section saves from blanking contact names when
 *   the submitted payload omits name fields
 * - 2026-08-25: Applies the shared Note limit to existing Prospect Contacts
 */

import { Contact } from '../../models/Contact';
import { useNavigate } from 'react-router';
import SchemaBasedDetails from '../common/SchemaBasedDetails';
import { useNotification } from '../../context/NotificationContext';
import { canDelete, canEdit } from '../../utils/permissions';
import { useFilteredSchemas } from '../../hooks/useFilteredSchemas';
import { useTeams } from '../../context/TeamContext';
import { PERMISSION_MESSAGES } from '../../constants/messages';
import { useDeleteContactMutation, useUpdateContactMutation } from '../../query/contactQueries';
import type { FormField } from '../common/FormField';
import { applyProspectContactNoteLimit } from '../../utils/schemaFieldLimits';

interface ContactDetailsProps {
  contact: Contact;
}

const franchiseContactRoleOptions: NonNullable<FormField['options']> = [
  { label: 'Select role', value: '' },
  { label: 'Owner', value: 'Owner' },
  { label: 'Broker of record', value: 'Broker of record' },
  { label: 'Real Estate Agent', value: 'Real Estate Agent' },
];

const ContactDetails: React.FC<ContactDetailsProps> = ({ contact }) => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { currentUserId, teams } = useTeams();
  const updateContact = useUpdateContactMutation();
  const deleteContact = useDeleteContactMutation();
  const { schemas: filteredSchemas, isLoading } = useFilteredSchemas(1, contact); // Type 1 for contacts

  // Check permissions using the new utility
  const hasDeletePermission = canDelete(contact, currentUserId);
  const hasContactEditPermission = canEdit(contact, teams, currentUserId);
  const constrainedSchemas = filteredSchemas.map((schema) =>
    schema.name.trim().toLowerCase() === 'prospect contact'
      ? applyProspectContactNoteLimit(schema)
      : schema
  );
  const accessibleSchemas = constrainedSchemas.flatMap((schema) => {
    const isFranchiseContact = schema.name.trim().toLowerCase() === 'franchise contact';
    if (!isFranchiseContact) {
      return [schema];
    }

    const hasFranchiseAccess = teams.some((team) => team.teamId === schema.teamId);
    if (!hasFranchiseAccess) {
      return [];
    }

    const fields = JSON.parse(schema.schema) as FormField[];

    return [{
      ...schema,
      schema: JSON.stringify(fields.map((field) => field.name === 'role'
        ? {
          ...field,
          type: 'select',
          required: false,
          options: franchiseContactRoleOptions,
        }
        : field)),
      isEditable: hasContactEditPermission,
    }];
  });
  const hasEditPermission = accessibleSchemas.some(s => s.isEditable);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const fallbackContactName = contact.displayName
    || contact.name
    || [contact.firstName, contact.lastName]
      .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
      .join(' ')
    || '';

  const handleSubmit = async (data: any) => {
    try {
      await updateContact.mutateAsync({
        id: contact.id,
        name: (typeof data.name === 'string' && data.name.trim() !== ''
          ? data.name
          : typeof data.displayName === 'string' && data.displayName.trim() !== ''
            ? data.displayName
            : fallbackContactName),
        data: JSON.stringify(data),
      });
      showNotification('Contact updated successfully', 'success');
    } catch (error) {
      console.error('Error updating contact:', error);
      showNotification('Failed to update contact', 'error');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      try {
        await deleteContact.mutateAsync({ id: contact.id });
        showNotification('Contact deleted successfully', 'success');
        navigate('/contacts');
      } catch (error) {
        console.error('Error deleting contact:', error);
        showNotification('Failed to delete contact', 'error');
      }
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      window.location.reload();
    }
  };

  return (
    <div>
      {/* Permission indicator - only show for limited access */}
      {!hasEditPermission && !contact.rlpCompanyId && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-800 font-medium">
              {PERMISSION_MESSAGES.LIMITED_ACCESS}
            </span>
          </div>
        </div>
      )}

      <SchemaBasedDetails
        key={contact.id}
        schemas={accessibleSchemas}
        data={contact}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onCancel={handleCancel}
        isDeleteEnabled={hasDeletePermission}
        isDisabled={(schema: any) => !schema.isEditable}
        fieldTextSize="sm"
        compactRowSpacing
        constrainHeight={false}
        constrainFieldGrid={false}
      />
    </div>
  );
};

export default ContactDetails;
